import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const StageStatus = {
    Pending: "p",
    Active: "a",
    Complete: "c",
    Skipped: "s"
};
export type StageStatusType = typeof StageStatus[keyof typeof StageStatus];
class MatterStage extends Base implements EntityType {
    matterStageId: uuid;
    matterId: uuid;
    caseTypeStageId?: uuid | null;
    name: string;
    sortOrder: number;
    stageStatus: StageStatusType;
    enteredOn?: Date | null;
    completedOn?: Date | null;
    constructor(doc: typeof MatterStage | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            matterStageId: genUUID(),
            matterId: null,
            name: null,
            sortOrder: null,
            stageStatus: "p",
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "matterStage";
        // @ts-ignore
        this.matterStageId = doc._matterStageId || doc.matterStageId || doc.matterstageid || genUUID();
        // @ts-ignore
        this.matterId = doc._matterId || doc.matterId || doc.matterid || null;
        // @ts-ignore
        this.caseTypeStageId = doc._caseTypeStageId || doc.caseTypeStageId || doc.casetypestageid || null;
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.sortOrder = util.coalesce(util.parseInt(doc._sortOrder), util.parseInt(doc.sortOrder), util.parseInt(doc.sortorder), null);
        // @ts-ignore
        this.stageStatus = util.trimRight(doc._stageStatus) || util.trimRight(doc.stageStatus) || util.trimRight(doc.stagestatus) || "p";
        // @ts-ignore
        this.enteredOn = DateUtil.timestampOnly(doc._enteredOn) || DateUtil.timestampOnly(doc.enteredOn) || DateUtil.timestampOnly(doc.enteredon) || null;
        // @ts-ignore
        this.completedOn = DateUtil.timestampOnly(doc._completedOn) || DateUtil.timestampOnly(doc.completedOn) || DateUtil.timestampOnly(doc.completedon) || null;
    }
    get id(): uuid { return this.matterStageId; }
    static STAGE_STATUS_VALUES = ["p", "a", "c", "s"];
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this matter stage"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.matterStageId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): MatterStage[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'matterStage',
    dataArea: 'Cases',
    attachedTo: {parents: ['matter'], via: 'matterId', onDelete: 'delete', onReplace: 'move'},
    table: 'MATTER_STAGE',
    comment: `A per-case instance of a workflow stage, copied from a CaseTypeStage template when the case is created. Each instance tracks the stage's current status (pending, active, complete, or skipped) and timestamps for when the case entered and left the stage, giving the firm a history of how long each phase took.`,
    fields: {
        matterStageId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this matter stage'
        },

        /**
         * The case this stage belongs to
         */
        matterId: {
            type: 'uuid',
            required: true,
            entityType: 'matter',
            comment: 'The case this stage belongs to'
        },

        /**
         * Reference back to the template this stage was created from.
         * May be null if the stage was added ad-hoc (not from a template).
         */
        caseTypeStageId: {
            type: 'uuid',
            entityType: 'caseTypeStage',
            comment: 'The case type stage template this was created from'
        },

        /**
         * Snapshotted from the template at creation time. Can be edited per-case.
         */
        name: {
            type: 'string',
            required: true,
            comment: 'Display name for this stage (snapshotted from template)'
        },

        /**
         * Display order within the case. Copied from template but can be reordered per-case.
         */
        sortOrder: {
            type: 'int',
            required: true,
            comment: 'Display order within the case (lower = earlier)'
        },

        /**
         * Current status of this stage within the case.
         * [p]ending - not yet reached
         * [a]ctive - case is currently in this stage
         * [c]omplete - case has passed through this stage
         * [s]kipped - stage was skipped
         */
        stageStatus: {
            type: 'char',
            required: true,
            values: ['p', 'a', 'c', 's'],
            valueNames: ['Pending', 'Active', 'Complete', 'Skipped'],
            default: 'p',
            comment: 'Current status: [p]ending, [a]ctive, [c]omplete, [s]kipped'
        },

        /**
         * When the case entered this stage (stageStatus changed to 'a')
         */
        enteredOn: {
            type: 'timestamp',
            comment: 'When the case entered this stage'
        },

        /**
         * When the case left this stage (stageStatus changed from 'a' to 'c' or 's')
         */
        completedOn: {
            type: 'timestamp',
            comment: 'When the case completed this stage. Not set if the stage is skipped.'
        }
    },
    indexes: [
        {name: 'notDeleted', fn: s => s.status !== 'D'},
        {name: 'active', fn: s => s.stageStatus === 'a' && s.status !== 'D'},
        {name: 'complete', fn: s => s.stageStatus === 'c' && s.status !== 'D'}
    ],
    maps: [
        {
            name: 'matterId',
            keysFn: s => s.matterId,
            presentFn: (s, k) => s.matterId === k && s.status !== 'D'
        },
        {
            name: 'caseTypeStageId',
            keysFn: s => s.caseTypeStageId,
            presentFn: (s, k) => s.caseTypeStageId === k && s.status !== 'D'
        }
    ]
});
    }
}
export default MatterStage;
